import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Helmet } from "react-helmet-async";
import { pushEvent } from "@/lib/gtm";
import { trackEvent } from "@/lib/analytics";
import {
  CheckCircle2,
  Loader2,
  Sparkles,
  AlertCircle,
  Crown,
  Zap,
  Mail,
  FileDown,
  TrendingUp,
  RefreshCw,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";

const MAX_RETRIES = 6;
const RETRY_INTERVAL_MS = 2000;

function fireProPurchaseEvent(): void {
  const params = new URLSearchParams(window.location.search);
  const eventId = params.get("conversion_event_id");
  if (!eventId) return;
  const value = parseFloat(params.get("plan_value") ?? "");
  const currency = params.get("plan_currency") ?? "USD";
  pushEvent("Purchase", {
    event_id: eventId,
    ...(isNaN(value) ? {} : { value }),
    currency,
  });
}

const PRO_HIGHLIGHTS = [
  { icon: TrendingUp, label: "Real-time margin tracking on every quote" },
  { icon: Mail, label: "Send quotes & invoices via email with PDF" },
  { icon: Layers, label: "Full cost catalog & job costing" },
  { icon: FileDown, label: "Branded PDFs with custom logo & footer" },
  { icon: RefreshCw, label: "Recurring invoices with auto-send" },
  { icon: Zap, label: "Import & export your data anytime" },
];

export default function BillingSuccessPage() {
  usePageTitle("Billing Confirmed");

  const { refreshUser, user } = useAuth();
  const [, navigate] = useLocation();
  const [activated, setActivated] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    fireProPurchaseEvent();
  }, []);

  const isLoggedIn = !!user;
  const isPro = user?.plan === "pro";

  useEffect(() => {
    if (!isLoggedIn) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      while (!stoppedRef.current && attemptsRef.current < MAX_RETRIES) {
        attemptsRef.current += 1;
        await refreshUser();

        if (stoppedRef.current) return;

        if (attemptsRef.current < MAX_RETRIES) {
          await new Promise<void>((resolve) => {
            timeoutId = setTimeout(resolve, RETRY_INTERVAL_MS);
          });
        }
      }

      if (!stoppedRef.current) {
        setTimedOut(true);
        setActivated(true);
      }
    }

    poll();

    return () => {
      stoppedRef.current = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [refreshUser, isLoggedIn]);

  useEffect(() => {
    if (isPro && !stoppedRef.current) {
      stoppedRef.current = true;
      setActivated(true);
      // Fire Plausible "Paid" event once per browser to enable filter-aware funnel analytics
      if (!localStorage.getItem("qt_paid")) {
        localStorage.setItem("qt_paid", "1");
        trackEvent("Paid");
      }
    }
  }, [isPro]);

  useEffect(() => {
    if (!activated || !isLoggedIn) return;
    const timer = setTimeout(() => navigate("/"), 4000);
    return () => clearTimeout(timer);
  }, [activated, isLoggedIn, navigate]);

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <div className="min-h-screen flex flex-col" style={{ background: "#f8fafc" }}>
      <nav
        style={{
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          position: "sticky",
          top: 0,
          zIndex: 50,
          padding: "0 24px",
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link href="/">
          <img src="/logo-new.png" alt="QuoTrak" style={{ height: "3rem", cursor: "pointer" }} />
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {isLoggedIn ? (
            <Link href="/">
              <Button variant="outline" size="sm">Go to Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link href="/login">
                <Button variant="outline" size="sm">Sign In</Button>
              </Link>
              <Link href="/signup">
                <Button size="sm">Get Started</Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-2xl mx-auto text-center space-y-8">
          <div className="flex justify-center">
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 32px rgba(234,88,12,0.25)",
              }}
            >
              <Crown className="text-white" style={{ width: 48, height: 48 }} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="text-primary" style={{ width: 22, height: 22 }} />
              <h1 className="text-4xl font-bold tracking-tight">Welcome to Pro!</h1>
              <Sparkles className="text-primary" style={{ width: 22, height: 22 }} />
            </div>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              Your payment was successful. Every Pro feature is now unlocked and ready for you.
            </p>
          </div>

          {isLoggedIn && (
            <div
              className="flex items-center justify-center gap-2 text-sm"
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "10px 20px",
                display: "inline-flex",
                margin: "0 auto",
              }}
            >
              {activated && !timedOut ? (
                <>
                  <CheckCircle2 className="text-green-600" style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span className="text-green-800 font-medium">
                    Pro is active — redirecting you to the dashboard…
                  </span>
                </>
              ) : timedOut ? (
                <>
                  <AlertCircle className="text-amber-500" style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span className="text-amber-800 font-medium">
                    Payment received. Refresh if features are still locked.
                  </span>
                </>
              ) : (
                <>
                  <Loader2 className="text-green-600 animate-spin" style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span className="text-green-800 font-medium">Activating your account…</span>
                </>
              )}
            </div>
          )}

          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              padding: "32px",
              textAlign: "left",
            }}
          >
            <h2 className="font-semibold text-base mb-4" style={{ color: "#111827" }}>
              Everything that's now unlocked:
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 12,
              }}
            >
              {PRO_HIGHLIGHTS.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "rgba(234,88,12,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon className="text-primary" style={{ width: 16, height: 16 }} />
                  </div>
                  <span className="text-sm text-muted-foreground leading-tight pt-1">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {!isLoggedIn && (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                Sign in to your account to start using all your Pro features.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Link href="/login">
                  <Button size="lg" className="gap-2">
                    <Crown style={{ width: 18, height: 18 }} />
                    Sign In & Get Started
                  </Button>
                </Link>
                <Link href="/">
                  <Button size="lg" variant="outline">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {isLoggedIn && (
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link href="/">
                <Button size="lg" className="gap-2">
                  Go to Dashboard
                </Button>
              </Link>
              <Link href="/settings">
                <Button size="lg" variant="outline">
                  Manage Billing
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      <footer
        style={{
          textAlign: "center",
          padding: "24px",
          color: "#9ca3af",
          fontSize: 13,
          borderTop: "1px solid #f1f5f9",
        }}
      >
        © {new Date().getFullYear()} QuoTrak. All rights reserved.
      </footer>
    </div>
    </>
  );
}
